import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import getAdminFieldsService from '../services/adminFields';
import postDetailsService from '../services/postDetails';
import postDetailsProService from '../services/postDetailsPro';
import getTypesService from '../services/getTypes';
import { adminLogout } from '../services/user';

const AdminDashboard = () => {
  const [types, setTypes] = useState([]);
  const [fields, setFields] = useState([]);
  const [fieldCategory, setFieldCategory] = useState([]);
  const [fieldFormat, setFieldFormat] = useState('text');
  const [dropdownOptions, setDropdownOptions] = useState('');
  const [fieldIsCascade, setFieldIsCascade] = useState(false);
  const [fieldDependent, setFieldDependent] = useState(null);
  const [fieldDependentValue, setFieldDependentValue] = useState();
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [updatedFieldName, setUpdatedFieldName] = useState('');
  const [updatedFieldCategory, setUpdatedFieldCategory] = useState([]);
  const [updatedFieldFormat, setUpdatedFieldFormat] = useState('');
  const [updatedFieldDropdownOptions, setUpdatedFieldDropdownOptions] =
    useState('');
  const [updatedFieldCascade, setUpdatedFieldCascade] = useState();
  const [updatedFieldDependent, setUpdatedFieldDependent] = useState();
  const [updatedFieldDependentValue, setUpdatedFieldDependentValue] =
    useState();
  const [newFieldName, setNewFieldName] = useState('');
  const [postDetailsFields, setPostDetailsFields] = useState([]);
  const [editingPostDetailsFieldId, setEditingPostDetailsFieldId] =
    useState(null);
  const [updatedPostDetailsFieldName, setUpdatedPostDetailsFieldName] =
    useState('');
  const [newPostDetailsFieldName, setNewPostDetailsFieldName] = useState('');
  const [postDetailsProFields, setPostDetailsProFields] = useState([]);
  const [editingPostDetailsProFieldId, setEditingPostDetailsProFieldId] =
    useState(null);
  const [updatedPostDetailsProFieldName, setUpdatedPostDetailsProFieldName] =
    useState('');
  const [newPostDetailsProFieldName, setNewPostDetailsProFieldName] =
    useState('');

  const navigate = useNavigate();

  useEffect(() => {
    fetchFields();
    fetchTypes();
    fetchPostDetailsFields();
    fetchPostDetailsProFields();
  }, []);

  const handleLogout = async () => {
    // Call server-side logout to delete session from database
    await adminLogout();
    // Clear client-side data
    localStorage.removeItem('BMI-admin-is-logged');
    const isSecure = window.location.protocol === 'https:';
    document.cookie = `BMI-admin-session=; path=/; max-age=0; SameSite=Strict${isSecure ? '; secure' : ''}`;
    window.location.replace('/');
  };

  const fetchTypes = async () => {
    try {
      const types = await getTypesService.getTypes();
      setTypes(types);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchFields = async () => {
    try {
      const fields = await getAdminFieldsService.getAdminFields();
      setFields([...fields].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })));
    } catch (error) {
      console.error('Failed to fetch fields:', error.message);
    }
  };

  const fetchPostDetailsFields = async () => {
    try {
      const postDetailsFields = await postDetailsService.getpostDetails();
      setPostDetailsFields([...postDetailsFields].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })));
    } catch (error) {
      console.error('Failed to fetch fields:', error.message);
    }
  };

  const fetchPostDetailsProFields = async () => {
    try {
      const postDetailsProFields =
        await postDetailsProService.getpostDetailsPro();
      setPostDetailsProFields([...postDetailsProFields].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })));
    } catch (error) {
      console.error('Failed to fetch fields:', error.message);
    }
  };

  const handleUpdateField = async (id) => {
    try {
      const updatedField = {
        name: updatedFieldName,
        category: updatedFieldCategory,
        format: updatedFieldFormat,
        options: updatedFieldDropdownOptions,
      };
      await getAdminFieldsService.updateAdminField(id, updatedField);
      fetchFields();
      setEditingFieldId(null);
      setUpdatedFieldName('');
      setUpdatedFieldCategory([]);
    } catch (error) {
      console.error('Failed to update field:', error.message);
    }
  };

  const handlePostDetailsUpdateField = async (id) => {
    try {
      const PostDetailsUpdatedField = { name: updatedPostDetailsFieldName };
      await postDetailsService.updatepostDetails(id, PostDetailsUpdatedField);
      fetchPostDetailsFields();
      setEditingPostDetailsFieldId(null);
      setUpdatedPostDetailsFieldName('');
    } catch (error) {
      console.error('Failed to update field:', error.message);
    }
  };

  const handlePostDetailsProUpdateField = async (id) => {
    try {
      const PostDetailsProUpdatedField = {
        name: updatedPostDetailsProFieldName,
      };
      await postDetailsProService.updatepostDetailsPro(
        id,
        PostDetailsProUpdatedField
      );
      fetchPostDetailsProFields();
      setEditingPostDetailsProFieldId(null);
      setUpdatedPostDetailsProFieldName('');
    } catch (error) {
      console.error('Failed to update field:', error.message);
    }
  };

  const handleDeleteField = async (id, position) => {
    try {
      await getAdminFieldsService.deleteAdminField(id, position);
      fetchFields();
    } catch (error) {
      console.error('Failed to delete field:', error.message);
    }
  };

  const handlePostDetailsDeleteField = async (id) => {
    try {
      await postDetailsService.deletepostDetails(id);
      fetchPostDetailsFields();
    } catch (error) {
      console.error('Failed to delete field:', error.message);
    }
  };

  const handlePostDetailsProDeleteField = async (id) => {
    try {
      await postDetailsProService.deletepostDetailsPro(id);
      fetchPostDetailsProFields();
    } catch (error) {
      console.error('Failed to delete field:', error.message);
    }
  };

  const handleAddField = async () => {
    try {
      const trimmedName = newFieldName.trim();
      if (!trimmedName) {
        alert('Please enter a field name.');
        return;
      }
      const duplicate = fields.some(
        (f) => f.name.toLowerCase() === trimmedName.toLowerCase()
      );
      if (duplicate) {
        alert(`The field "${trimmedName}" already exists. Please use a different name.`);
        return;
      }
      const newField = {
        name: trimmedName,
        category: fieldCategory,
        format: fieldFormat || 'text',
        options: dropdownOptions,
        depend: fieldIsCascade
          ? [fieldDependent.name, fieldDependentValue]
          : false,
      };
      await getAdminFieldsService.addAdminField(newField);
      fetchFields();
      setNewFieldName('');
      setFieldCategory([]);
      setFieldFormat('');
      setDropdownOptions('');
      window.location.reload();
    } catch (error) {
      console.error('Failed to add field:', error.message);
    }
  };

  const handlePostDetailsAddField = async () => {
    try {
      const trimmedName = newPostDetailsFieldName.trim();
      if (!trimmedName) {
        alert('Please enter a field name.');
        return;
      }
      const duplicate = postDetailsFields.some(
        (f) => f.name.toLowerCase() === trimmedName.toLowerCase()
      );
      if (duplicate) {
        alert(`The field "${trimmedName}" already exists in Post Details. Please use a different name.`);
        return;
      }
      const newPostDetailsField = { name: trimmedName };
      await postDetailsService.addpostDetails(newPostDetailsField);
      fetchPostDetailsFields();
      setNewPostDetailsFieldName('');
    } catch (error) {
      console.error('Failed to add field:', error.message);
    }
  };

  const handlePostDetailsProAddField = async () => {
    try {
      const trimmedName = newPostDetailsProFieldName.trim();
      if (!trimmedName) {
        alert('Please enter a field name.');
        return;
      }
      const duplicate = postDetailsProFields.some(
        (f) => f.name.toLowerCase() === trimmedName.toLowerCase()
      );
      if (duplicate) {
        alert(`The field "${trimmedName}" already exists in Post Details Pro. Please use a different name.`);
        return;
      }
      const newPostDetailsProField = { name: trimmedName };
      await postDetailsProService.addpostDetailsPro(newPostDetailsProField);
      fetchPostDetailsProFields();
      setNewPostDetailsProFieldName('');
    } catch (error) {
      console.error('Failed to add field:', error.message);
    }
  };

  const startEditing = (field) => {
    setEditingFieldId(field._id);
    setUpdatedFieldName(field.name);
    setUpdatedFieldCategory(field.category || []);
    setUpdatedFieldFormat(field.format);
    setUpdatedFieldDropdownOptions(field.options);
  };

  const startPostDetailsEditing = (field) => {
    setEditingPostDetailsFieldId(field._id);
    setUpdatedPostDetailsFieldName(field.name);
  };

  const startPostDetailsProEditing = (field) => {
    setEditingPostDetailsProFieldId(field._id);
    setUpdatedPostDetailsProFieldName(field.name);
  };

  const handleCategoryChange = (category, setter) => {
    setter((prevCategories) =>
      prevCategories.includes(category)
        ? prevCategories.filter((item) => item !== category)
        : [...prevCategories, category]
    );
  };

  const BtnClass =
    'bg-white border border-gray-200 text-gray-600 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg shadow-md hover:bg-blue-100 hover:border-blue-300 hover:text-blue-600 transition-all duration-200 text-md sm:text-sm font-medium whitespace-nowrap';
  const sectionCard =
    'bg-white p-3 sm:p-4 md:p-6 rounded-xl shadow-md border border-gray-100 mb-4 sm:mb-6 md:mb-8';
  const inputClass =
    'w-full min-w-[80px] sm:min-w-[100px] border border-gray-300 rounded-md px-2 py-1.5 sm:py-2 focus:ring-2 focus:ring-blue-400 outline-none text-xs sm:text-sm bg-white text-gray-800 transition-all';
  const labelClass =
    'block text-xs sm:text-sm font-semibold text-gray-600 mb-1';
  const primaryBtn =
    'bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors shadow-sm disabled:opacity-50 text-xs sm:text-sm';
  const tableHeader =
    'px-2 sm:px-3 md:px-4 py-2 sm:py-3 bg-gray-50 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider border-b whitespace-nowrap';
  const tableCell = 'px-2 sm:px-3 md:px-4 py-2 sm:py-3 text-xs sm:text-sm';

  return (
    <div className="admin-dashboard min-h-screen overflow-y-auto bg-gray-50 p-2 sm:p-4 md:p-6 lg:p-8 font-sans text-gray-800">
      {/* Admin Navigation Buttons */}
      <div className="mb-4 sm:mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-3 sm:mb-4">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-700">
            Admin Controls
          </h2>

          <button
            onClick={handleLogout}
            className="
              inline-flex
              items-center
              gap-1
              sm:gap-2
              px-2
              sm:px-3
              md:px-4
              py-1.5
              sm:py-2
              rounded-lg
              bg-red-50
              text-red-600
              border
              border-red-200
              text-md
              sm:text-md
              font-semibold
              hover:bg-red-100
              hover:text-red-700
              transition
            "
            aria-label="Logout"
          >
            <LogOut size={14} className="sm:w-4 sm:h-4" />
            Logout
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 sm:gap-2 md:gap-3">
          <button
            className={BtnClass}
            onClick={() => navigate('/contact-message')}
          >
            Contact Messages
          </button>
          <button
            className={BtnClass}
            onClick={() => navigate('/support-message')}
          >
            Support Messages
          </button>
          <button className={BtnClass} onClick={() => navigate('/discounts')}>
            Discounts
          </button>
          <button
            className={BtnClass}
            onClick={() => navigate('/chats-inspect')}
          >
            Inspect Chats
          </button>
          <button
            className={BtnClass}
            onClick={() => navigate('/users-inspect')}
          >
            Inspect Users
          </button>
          <button
            className={BtnClass}
            onClick={() => navigate('/sellers-verify')}
          >
            Inspect Sellers Verify Applications
          </button>
          <button className={BtnClass} onClick={() => navigate('/indexing')}>
            Adjust Field Positions
          </button>
          <button className={BtnClass} onClick={() => navigate('/sms-sender')}>
            Send SMS to User
          </button>
          <button
            className={BtnClass}
            onClick={() => navigate('/restrict-fields')}
          >
            Seller Only Fields
          </button>
          <button
            className={BtnClass}
            onClick={() => navigate('/service-pages')}
          >
            Service Pages
          </button>
          <button
            className={BtnClass}
            onClick={() => navigate('/career-job-update')}
          >
            Career Job Update
          </button>
          <button
            className={BtnClass}
            onClick={() => navigate('/encrypted-fields')}
          >
            Encrypted Fields
          </button>
          <button
            className={BtnClass}
            onClick={() => navigate('/posts-approval')}
          >
            Posts Approval
          </button>
          <button
            className={BtnClass}
            onClick={() =>
            (window.location.href =
              'https://www.beatmyinsurance.com/admin-login')
            }
          >
            Login as User
          </button>
          <button
            className={BtnClass}
            onClick={() => navigate('/upload-claiming-profiles')}
          >
            Upload Claiming Profiles
          </button>
          <button className={BtnClass} onClick={() => navigate('/brokers')}>
            Claiming Profiles
          </button>
          <button
            className={BtnClass}
            onClick={() => navigate('/upload-images')}
          >
            Upload Images
          </button>
          <button
            className={BtnClass}
            onClick={() => navigate('/upload-tool-data')}
          >
            Upload Tool Data
          </button>
          <button
            className={BtnClass}
            onClick={() => navigate('/feedbacks')}
          >
            User Feedbacks
          </button>
          <button
            className={BtnClass}
            onClick={() => navigate('/feature-toggles')}
          >
            Feature Toggles
          </button>
        </div>
      </div>
      {/* New Post Fields Control Section */}
      <div className={sectionCard}>
        <h1 className="text-base sm:text-lg md:text-xl font-bold mb-3 sm:mb-4 md:mb-6 border-b pb-3 sm:pb-4 text-gray-700">
          New Post Fields Control
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          <div className="space-y-2 sm:space-y-4">
            <div>
              <label className={labelClass}>New Field Name</label>
              <input
                type="text"
                className={inputClass}
                value={newFieldName}
                onChange={(e) => setNewFieldName(e.target.value)}
                placeholder="New field name"
              />
            </div>
            <div>
              <label className={labelClass}>Select Format</label>
              <select
                className={inputClass}
                value={fieldFormat}
                onChange={(e) => setFieldFormat(e.target.value)}
              >
                <option value="text">Text</option>
                <option value="dropdown">Dropdown</option>
                <option value="date">Date</option>
              </select>
            </div>
          </div>

          <div className="space-y-2 sm:space-y-4">
            <div>
              <label className={labelClass}>Field Category</label>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-2 sm:gap-3 max-h-38 sm:max-h-38 overflow-y-auto border p-2 sm:p-3 rounded-lg bg-gray-50">
                {types &&
                  types.map((type) => (
                    <div
                      key={type}
                      className="flex items-center space-x-1 sm:space-x-2"
                    >
                      <input
                        type="checkbox"
                        className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                        id={`add-category-${type}`}
                        value={type}
                        checked={fieldCategory.includes(type)}
                        onChange={() =>
                          handleCategoryChange(type, setFieldCategory)
                        }
                      />
                      <label
                        htmlFor={`add-category-${type}`}
                        className="text-md text-gray-600 truncate"
                      >
                        {type}
                      </label>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <div className="space-y-2 sm:space-y-4">
            {fieldFormat === 'dropdown' && (
              <div>
                <label className={labelClass}>Dropdown Options</label>
                <input
                  type="text"
                  className={inputClass}
                  value={dropdownOptions}
                  onChange={(e) => setDropdownOptions(e.target.value)}
                  placeholder="Option 1, Option 2..."
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Cascade?</label>
                <select
                  className={inputClass}
                  value={fieldIsCascade}
                  onChange={(e) => {
                    setFieldIsCascade(e.target.value === 'true');
                    console.log(e.target.value, fieldIsCascade);
                  }}
                >
                  <option value={false}>False</option>
                  <option value={true}>True</option>
                </select>
              </div>

              {fieldIsCascade && (
                <div>
                  <label className={labelClass}>Depend field</label>
                  <select
                    className={inputClass}
                    value={fieldDependent ? fieldDependent.name : ''}
                    onChange={(e) => {
                      const selectedField = fields.find(
                        (field) => field.name === e.target.value
                      );
                      setFieldDependent(selectedField);
                      console.log(selectedField._id, selectedField.name);
                    }}
                  >
                    <option value={null}>Select Field</option>
                    {fields &&
                      fields.map((field) => (
                        <option key={field.name} value={field.name}>
                          {field.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}
            </div>

            {fieldDependent && fieldIsCascade && (
              <div>
                <label className={labelClass}>Depend value</label>
                {fieldDependent.format === 'dropdown' ? (
                  <select
                    className={inputClass}
                    onChange={(e) => setFieldDependentValue(e.target.value)}
                  >
                    <option value={null}>Select Value</option>
                    {fieldDependent.options?.split(',').map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    className={inputClass}
                    onChange={(e) => setFieldDependentValue(e.target.value)}
                  >
                    <option value={null}>Select</option>
                    <option value={'X---Any_Value---X'}>Any Value</option>
                  </select>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 sm:mt-6 md:mt-8 pt-3 sm:pt-4 border-t flex justify-end">
          <button className={primaryBtn} onClick={handleAddField}>
            + Add New Field
          </button>
        </div>
      </div>
      {/* Active Post Fields Table */}
      <div
        className={`${sectionCard} h-auto max-h-[350px] sm:max-h-[450px] md:max-h-[500px] lg:h-[600px] lg:max-h-none flex flex-col overflow-hidden`}
      >
        <h2 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-3 md:mb-4 text-gray-700">
          Active Post Fields
        </h2>
        <div className="overflow-auto flex-1 rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className={tableHeader}>Field Name</th>
                <th className={tableHeader}>Category</th>
                <th className={tableHeader}>Format</th>
                <th className={tableHeader}>Dropdown Options</th>
                <th className={tableHeader}>Cascade?</th>
                <th className={tableHeader}>Depend Field</th>
                <th className={tableHeader}>Depend Value</th>
                <th className={tableHeader}>Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {fields &&
                fields.map((field) => (
                  <tr
                    key={field._id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className={tableCell}>
                      {editingFieldId === field._id ? (
                        <input
                          type="text"
                          className={inputClass}
                          value={updatedFieldName}
                          onChange={(e) => setUpdatedFieldName(e.target.value)}
                        />
                      ) : (
                        field.name
                      )}
                    </td>
                    <td className={tableCell}>
                      {editingFieldId === field._id ? (
                        <div className="max-h-24 overflow-y-auto border p-1 rounded">
                          {types &&
                            types.map((type) => (
                              <div
                                key={type}
                                className="flex items-center space-x-1 text-xs"
                              >
                                <input
                                  type="checkbox"
                                  id={`edit-category-${type}`}
                                  value={type}
                                  checked={updatedFieldCategory.includes(type)}
                                  onChange={() =>
                                    handleCategoryChange(
                                      type,
                                      setUpdatedFieldCategory
                                    )
                                  }
                                />
                                <label htmlFor={`edit-category-${type}`}>
                                  {type}
                                </label>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <span className="text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded-full font-medium">
                          {field.category.join(', ')}
                        </span>
                      )}
                    </td>
                    <td className={`${tableCell} capitalize`}>
                      {editingFieldId === field._id ? (
                        <select
                          className={inputClass}
                          value={updatedFieldFormat}
                          onChange={(e) =>
                            setUpdatedFieldFormat(e.target.value)
                          }
                        >
                          <option value="text">Text</option>
                          <option value="dropdown">Dropdown</option>
                          <option value="date">Date</option>
                        </select>
                      ) : (
                        field.format
                      )}
                    </td>
                    <td className={`${tableCell} max-w-xs truncate`}>
                      {editingFieldId === field._id &&
                        updatedFieldFormat === 'dropdown' ? (
                        <input
                          type="text"
                          className={inputClass}
                          value={updatedFieldDropdownOptions}
                          onChange={(e) =>
                            setUpdatedFieldDropdownOptions(e.target.value)
                          }
                        />
                      ) : (
                        field.options || '-'
                      )}
                    </td>
                    <td className={`${tableCell} text-center font-medium`}>
                      {editingFieldId === field._id &&
                        field.depend !== undefined ? (
                        <select
                          className={inputClass}
                          onChange={(e) =>
                            setUpdatedFieldCascade(e.target.value)
                          }
                        >
                          <option value={false}>Select</option>
                          <option value={true}>True</option>
                          <option value={false}>False</option>
                        </select>
                      ) : (
                        'False'
                      )}
                    </td>
                    <td className={tableCell}>
                      {editingFieldId === field._id &&
                        field.depend !== undefined &&
                        field.depend ? (
                        <select
                          onChange={(e) => setFieldDependent(e.target.value)}
                        >
                          <option value={null}>Select</option>
                          {fields.map((fieldOpt) => (
                            <option key={fieldOpt.name} value={fieldOpt}>
                              {fieldOpt.name}
                            </option>
                          ))}
                        </select>
                      ) : field.depend ? (
                        field.depend[0]
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className={tableCell}>
                      {/*editingFieldId === field._id && field.depend !== undefined &&
                    field.depend ? (
                      updatedFieldDependent ? (
                        updatedFieldDependent.format === "dropdown" ? (
                          <select>
                           updatedFieldDependent.options.split(",").map((valueOpt)=>(
                              <option value={valueOpt} key={valueOpt}>{valueOpt}</option>
                            
                          ))
                          </select>
                        ):
                        <select>
                          <option>HIHIPH</option>
                        </select>
                      )
                    ) : (
                      field.depend ? field.depend[1] : "-"
                    )*/}
                    </td>

                    <td className={`${tableCell} space-x-4 text-right`}>
                      <div className="flex justify-end gap-1 sm:gap-2 md:gap-4">
                        {editingFieldId === field._id ? (
                          <>
                            <button
                              className={BtnClass}
                              onClick={() => handleUpdateField(field._id)}
                            >
                              Save
                            </button>
                            <button
                              className={BtnClass}
                              onClick={() => setEditingFieldId(null)}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className={BtnClass}
                              onClick={() => startEditing(field)}
                            >
                              Edit
                            </button>
                            <button
                              className={BtnClass}
                              onClick={() =>
                                handleDeleteField(
                                  field._id,
                                  fields.indexOf(field)
                                )
                              }
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
        {/* Post Details Fields Table */}
        <div
          className={`${sectionCard} h-auto max-h-[300px] sm:max-h-[400px] md:max-h-[500px] lg:h-[600px] lg:max-h-none flex flex-col overflow-hidden`}
        >
          <h2 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-3 md:mb-4 text-gray-700">
            Post Details Fields
          </h2>
          <div className="flex flex-col sm:flex-row gap-2 mb-2 sm:mb-4">
            <input
              type="text"
              className={inputClass}
              value={newPostDetailsFieldName}
              onChange={(e) => setNewPostDetailsFieldName(e.target.value)}
              placeholder="New field name"
            />
            <button
              className="bg-blue-600 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm whitespace-nowrap"
              onClick={handlePostDetailsAddField}
            >
              + Add Field
            </button>
          </div>
          <div className="overflow-auto flex-1 border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className={tableHeader}>Field Name</th>
                  <th className={`${tableHeader} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {postDetailsFields &&
                  postDetailsFields.map((field) => (
                    <tr key={field._id} className="hover:bg-gray-50">
                      <td className={tableCell}>
                        {editingPostDetailsFieldId === field._id ? (
                          <input
                            type="text"
                            className={inputClass}
                            value={updatedPostDetailsFieldName}
                            onChange={(e) =>
                              setUpdatedPostDetailsFieldName(e.target.value)
                            }
                          />
                        ) : (
                          field.name
                        )}
                      </td>
                      <td className={`${tableCell} text-right`}>
                        <div className="flex justify-end gap-1 sm:gap-2 md:gap-3">
                          {editingPostDetailsFieldId === field._id ? (
                            <>
                              <button
                                className={BtnClass}
                                onClick={() =>
                                  handlePostDetailsUpdateField(field._id)
                                }
                              >
                                Save
                              </button>
                              <button
                                className={BtnClass}
                                onClick={() =>
                                  setEditingPostDetailsFieldId(null)
                                }
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className={BtnClass}
                                onClick={() => startPostDetailsEditing(field)}
                              >
                                Edit
                              </button>
                              <button
                                className={BtnClass}
                                onClick={() =>
                                  handlePostDetailsDeleteField(field._id)
                                }
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* Post Details Pro Fields Table */}
        <div
          className={`${sectionCard} h-auto max-h-[300px] sm:max-h-[400px] md:max-h-[500px] lg:h-[600px] lg:max-h-none flex flex-col overflow-hidden`}
        >
          <h2 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-3 md:mb-4 text-gray-700">
            Post Details Pro Fields
          </h2>
          <div className="flex flex-col sm:flex-row gap-2 mb-2 sm:mb-4">
            <input
              type="text"
              className={inputClass}
              value={newPostDetailsProFieldName}
              onChange={(e) => setNewPostDetailsProFieldName(e.target.value)}
              placeholder="New pro field name"
            />
            <button
              className="bg-blue-600 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm whitespace-nowrap"
              onClick={handlePostDetailsProAddField}
            >
              + Add Field
            </button>
          </div>
          <div className="overflow-auto flex-1 border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className={tableHeader}>Field Name</th>
                  <th className={`${tableHeader} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {postDetailsProFields &&
                  postDetailsProFields.map((field) => (
                    <tr key={field._id} className="hover:bg-gray-50">
                      <td className={tableCell}>
                        {editingPostDetailsProFieldId === field._id ? (
                          <input
                            type="text"
                            className={inputClass}
                            value={updatedPostDetailsProFieldName}
                            onChange={(e) =>
                              setUpdatedPostDetailsProFieldName(e.target.value)
                            }
                          />
                        ) : (
                          field.name
                        )}
                      </td>
                      <td className={`${tableCell} text-right`}>
                        <div className="flex justify-end gap-1 sm:gap-2 md:gap-3">
                          {editingPostDetailsProFieldId === field._id ? (
                            <>
                              <button
                                className={BtnClass}
                                onClick={() =>
                                  handlePostDetailsProUpdateField(field._id)
                                }
                              >
                                Save
                              </button>
                              <button
                                className={BtnClass}
                                onClick={() =>
                                  setEditingPostDetailsProFieldId(null)
                                }
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className={BtnClass}
                                onClick={() =>
                                  startPostDetailsProEditing(field)
                                }
                              >
                                Edit
                              </button>
                              <button
                                className={BtnClass}
                                onClick={() =>
                                  handlePostDetailsProDeleteField(field._id)
                                }
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
